/**
 * Endpoint - Shared form fields for endpoint configuration
 *
 * Used by both EndpointPopup (in graph nodes) and NodeInspector (in side panel).
 * Includes expose/unexpose API controls when inside a definition.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { EndpointState } from '@/nodes/Endpoint';
import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { extractAdoptedGet } from '../../shared/utils';
import { logger } from '@/logging';

interface EndpointProps {
  instanceId: string
  nodeId: string;
  nodeType: string;
  endpoint: EndpointState;
  onClose?: () => void;
}

export function EndpointConfig({
  instanceId,
  nodeId,
  nodeType,
  endpoint,
  onClose
}: EndpointProps) {

  const { id: endpointId,
    isInput,
    isOutput,
    allowInput,
    allowOutput,
    scaleIncomingValues,
    inheritMetadata,
  } = endpoint;

  const { setEndpointMetadata, hasDefinition, getPayload } = useRuntime()

  // Check if this node is a graph node (sub-graph) - its type is a graph definition, not a primitive
  const isGraphNode = hasDefinition(nodeType)

  // Detect adopted getter from upstream connection — extract expression from stringified function
  const payload = getPayload(instanceId, nodeId, endpointId);
  const adoptedGet = useMemo(() => extractAdoptedGet(endpoint.useExp, payload?.get), [endpoint.useExp, payload?.get]);

  const onChange = useCallback((key: string, value: unknown) => {
    logger.trace('EndpointConfig', `onChange: ${nodeId}.${endpointId} ${key}=${JSON.stringify(value)}`);
    setEndpointMetadata(instanceId, nodeId, endpoint.id, key, value)
  }, [setEndpointMetadata, instanceId, nodeId, endpoint.id])

  const isNumeric = endpoint.dataType === 'number';
  const isArray = endpoint.dataType === 'array';
  const hasRangeFields = isNumeric || isArray;

  // Local state for numeric fields (submit on Enter/blur)
  const [minText, setMinText] = useState(() => endpoint.min?.toString() ?? '');
  const [maxText, setMaxText] = useState(() => endpoint.max?.toString() ?? '');
  const [stepText, setStepText] = useState(() => endpoint.step?.toString() ?? '');
  const [lengthText, setLengthText] = useState(() => endpoint.length?.toString() ?? '');

  useEffect(() => {
    setMinText(endpoint.min?.toString() ?? '');
    setMaxText(endpoint.max?.toString() ?? '');
    setStepText(endpoint.step?.toString() ?? '');
    setLengthText(endpoint.length?.toString() ?? '');
  }, [endpoint.min, endpoint.max, endpoint.step, endpoint.length]);

  const submitNumeric = (key: 'min' | 'max' | 'step' | 'length', value: string) => {
    const trimmed = value.trim();
    if (trimmed === '') {
      onChange(key, undefined);
    } else {
      const num = Number(trimmed);
      if (isNaN(num)) {
        if (key === 'min') setMinText(endpoint.min?.toString() ?? '');
        if (key === 'max') setMaxText(endpoint.max?.toString() ?? '');
        if (key === 'step') setStepText(endpoint.step?.toString() ?? '');
        if (key === 'length') setLengthText(endpoint.length?.toString() ?? '');
      } else {
        onChange(key, key === 'length' ? Math.max(0, Math.round(num)) : num);
      }
    }
  };

  // Local state for options text (allows typing commas)
  const [optionsText, setOptionsText] = useState(() => (endpoint.options ?? []).join(', '));
  const [showOptionsInput, setShowOptionsInput] = useState(() => (endpoint.options ?? []).length > 0);
  useEffect(() => {
    setOptionsText((endpoint.options ?? []).join(', '));
    setShowOptionsInput((endpoint.options ?? []).length > 0);
  }, [endpoint.options]);

  return (
    <>
      {/* Input/Output toggles and useExp */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
        {allowInput && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9ca3af', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isInput}
              onChange={e => onChange('isInput', e.target.checked)}
              style={{ width: 14, height: 14, accentColor: '#3b82f6' }}
            />
            Input
          </label>
        )}
        {allowOutput && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9ca3af', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isOutput}
              onChange={e => onChange('isOutput', e.target.checked)}
              style={{ width: 14, height: 14, accentColor: '#3b82f6' }}
            />
            Output
          </label>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9ca3af', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={endpoint.readonly ?? false}
            onChange={e => onChange('readonly', e.target.checked)}
            style={{ width: 14, height: 14, accentColor: '#3b82f6' }}
          />
          Readonly
        </label>
        {(isNumeric || endpoint.dataType === 'array' || !!endpoint.exp || adoptedGet) && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: adoptedGet ? '#6b7280' : '#9ca3af', cursor: adoptedGet ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={!!endpoint.useExp || !!adoptedGet}
              disabled={!!adoptedGet}
              onChange={e => {
                const on = e.target.checked;
                onChange('useExp', on);
                if (on && !endpoint.exp) {
                  const defaultExpr = endpoint.dataFlow === 'event'
                    ? 'host.pattern.value[host.index.value]'
                    : 'value';
                  onChange('exp', defaultExpr);
                }
              }}
              style={{ width: 14, height: 14, accentColor: adoptedGet ? '#6b7280' : '#3b82f6' }}
            />
            ƒ(t)
          </label>
        )}
      </div>

      {/* Range fields - from/to/step (+ length for arrays) with labels above */}
      {(hasRangeFields && !endpoint.options?.length) && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 9, color: '#6b7280', display: 'block', marginBottom: 2 }}>from</label>
            <input
              type="text"
              inputMode="decimal"
              value={minText}
              placeholder="—"
              onChange={e => setMinText(e.target.value)}
              onBlur={() => submitNumeric('min', minText)}
              onKeyDown={e => { if (e.key === 'Enter') { submitNumeric('min', minText); e.currentTarget.blur(); }}}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 9, color: '#6b7280', display: 'block', marginBottom: 2 }}>to</label>
            <input
              type="text"
              inputMode="decimal"
              value={maxText}
              placeholder="—"
              onChange={e => setMaxText(e.target.value)}
              onBlur={() => submitNumeric('max', maxText)}
              onKeyDown={e => { if (e.key === 'Enter') { submitNumeric('max', maxText); e.currentTarget.blur(); }}}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 9, color: '#6b7280', display: 'block', marginBottom: 2 }}>step</label>
            <input
              type="text"
              inputMode="decimal"
              value={stepText}
              placeholder="—"
              onChange={e => setStepText(e.target.value)}
              onBlur={() => submitNumeric('step', stepText)}
              onKeyDown={e => { if (e.key === 'Enter') { submitNumeric('step', stepText); e.currentTarget.blur(); }}}
              style={inputStyle}
            />
          </div>
          {isArray && (
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 9, color: '#6b7280', display: 'block', marginBottom: 2 }}>length</label>
              <input
                type="text"
                inputMode="numeric"
                value={lengthText}
                placeholder="—"
                onChange={e => setLengthText(e.target.value)}
                onBlur={() => submitNumeric('length', lengthText)}
                onKeyDown={e => { if (e.key === 'Enter') { submitNumeric('length', lengthText); e.currentTarget.blur(); }}}
                style={inputStyle}
              />
            </div>
          )}
        </div>
      )}

      {/* Options (for number, string on graph node API, or color types) */}
      {(endpoint.dataType === 'number' || (endpoint.dataType === 'string' && isGraphNode) || endpoint.dataType === 'color') && (
        <div style={{ marginBottom: 10 }}>
          <label
            onClick={() => {
              if (showOptionsInput) {
                // Save and close
                const parsed = optionsText.split(',').map(s => s.trim()).filter(Boolean);
                const validOpts = isNumeric
                  ? parsed.map(Number).filter(n => !isNaN(n))
                  : parsed;
                onChange('options', validOpts.length > 0 ? validOpts : undefined);
                setShowOptionsInput(false);
              } else {
                setOptionsText((endpoint.options ?? []).join(', '));
                setShowOptionsInput(true);
              }
            }}
            style={{
              fontSize: 9,
              color: '#6b7280',
              display: 'block',
              marginBottom: 2,
              cursor: 'pointer'
            }}
          >
            options <span style={{ color: '#4b5563' }}>{showOptionsInput ? '−' : (endpoint.options?.length ? '(edit)' : '+')}</span>
          </label>
          {showOptionsInput && (
            <input
              type="text"
              inputMode={isNumeric ? 'decimal' : 'text'}
              value={optionsText}
              placeholder={endpoint.dataType === 'color' ? '#ff0000, #00ff00...' : isNumeric ? '1, 2, 3...' : 'opt1, opt2...'}
              onChange={e => setOptionsText(e.target.value)}
              onBlur={() => {
                const parsed = optionsText.split(',').map(s => s.trim()).filter(Boolean);
                const validOpts = isNumeric
                  ? parsed.map(Number).filter(n => !isNaN(n))
                  : parsed;
                onChange('options', validOpts.length > 0 ? validOpts : undefined);
                setShowOptionsInput(false);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const parsed = optionsText.split(',').map(s => s.trim()).filter(Boolean);
                  const validOpts = isNumeric
                    ? parsed.map(Number).filter(n => !isNaN(n))
                    : parsed;
                  onChange('options', validOpts.length > 0 ? validOpts : undefined);
                  setShowOptionsInput(false);
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  setOptionsText((endpoint.options ?? []).join(', '));
                  setShowOptionsInput(false);
                  e.currentTarget.blur();
                }
              }}
              autoFocus
              style={inputStyle}
            />
          )}
        </div>
      )}

      {/* Autoscale and Inherit checkboxes on same row - only for numeric endpoints without options */}
      {isNumeric && !endpoint.options?.length && (isInput || isOutput) && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
          {isInput && (
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9ca3af', cursor: 'pointer' }}
              title="Scale incoming values to this endpoint's min/max range"
            >
              <input
                type="checkbox"
                checked={scaleIncomingValues ?? false}
                onChange={e => onChange('scaleIncomingValues', e.target.checked)}
                style={{ width: 14, height: 14, accentColor: '#3b82f6' }}
              />
              autoscale
            </label>
          )}
          {isOutput && (
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9ca3af', cursor: 'pointer' }}
              title="Copy min/max/step metadata from connected target endpoint"
            >
              <input
                type="checkbox"
                checked={inheritMetadata ?? false}
                onChange={e => onChange('inheritMetadata', e.target.checked)}
                style={{ width: 14, height: 14, accentColor: '#3b82f6' }}
              />
              inherit
            </label>
          )}
        </div>
      )}

      {/* Label field (only for graph nodes / sub-graphs) */}
      {(
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Label</label>
          <input
            type="text"
            value={endpoint.label}
            placeholder={endpointId}
            onChange={e => onChange('label', e.target.value)}
            style={{ ...inputStyle, width: '100%' }}
          />
        </div>
      )}

    </>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: '#374151',
  border: '1px solid #4b5563',
  borderRadius: 4,
  padding: '4px 8px',
  color: 'white',
  fontSize: 11
};
